'use client';

import type { FC, PropsWithChildren } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
// eslint-disable-next-line no-restricted-imports
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import {
  TRANSITION_DELAY_BASE,
  TRANSITION_DURATION_BASE,
} from '@/core/constants/tokens';
import useTranslations from '@/core/hooks/use-translations';
import { trackInteraction } from '@/core/services/ga4/ga4-service';
import Button from '@/ui/components/Button/Button';
import VectorGraphic from '@/ui/components/VectorGraphic/VectorGraphic';
import useUpdatedRef from '@/ui/hooks/use-updated-ref';
import cn from '@/ui/utils/cn/cn';

import $ from './Dialog.module.scss';

export type DialogProps = PropsWithChildren<{
  onClose?: () => void;
  variant?: 'default' | 'full-screen' | 'modal';
  withHeader?: boolean;
  withCancelButton?: boolean;
  ariaLabel?: string;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  closeButtonClassName?: string;
  ['data-t']?: string;
  ['data-testid']?: string;
}>;

const Dialog: FC<DialogProps> = ({
  children,
  variant = 'default',
  onClose,
  withHeader = false,
  withCancelButton = false,
  ariaLabel,
  className,
  headerClassName,
  contentClassName,
  closeButtonClassName,
  'data-t': dataT,
  'data-testid': dataTestId,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const close = () => {
    if (onClose === undefined) router.back();
    else onClose();
  };
  const onCloseRef = useUpdatedRef(close);

  const duration = TRANSITION_DURATION_BASE / 1000;

  const handleClose = () => {
    trackInteraction({
      interaction_type: 'click',
      click_target: 'close dialog',
      click_text: 'close',
    });

    setIsVisible(false);
  };

  useEffect(() => {
    const abortController = new AbortController();
    const dialog = dialogRef.current!;

    const handleDialogClick = (ev: Event) => {
      if ((ev.target as any)?.nodeName === 'DIALOG') {
        onCloseRef.current?.();
      }
    };

    const handleKeydown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape') {
        onCloseRef.current?.();
      }
    };

    dialog.addEventListener('click', handleDialogClick, {
      signal: abortController.signal,
    });
    window.addEventListener('keydown', handleKeydown, {
      signal: abortController.signal,
    });

    return () => {
      dialog.removeEventListener('click', handleDialogClick);
      window.removeEventListener('keydown', handleKeydown);

      abortController.abort();
    };
  }, [onCloseRef, variant]);

  const isModalVariant = variant === 'modal';
  const isFullScreen = variant === 'full-screen';

  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={close}
      presenceAffectsLayout={false}
    >
      {isVisible && (
        <motion.div
          className={$.backdrop}
          onClick={handleClose}
          role="presentation"
          tabIndex={-1}
          data-t={dataT}
          data-testid={dataTestId}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration }}
        >
          <RemoveScroll>
            <motion.dialog
              ref={dialogRef}
              key="dialog"
              className={cn(isModalVariant ? $.modal : $.dialog, className)}
              data-t={dataT}
              data-testid={dataTestId}
              aria-label={ariaLabel}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration,
              }}
              onClick={e => e.stopPropagation()}
              open
            >
              {isModalVariant ? (
                <>
                  {withHeader && (
                    <div className={cn($.header, headerClassName)}>
                      <div className={$.logoWrapper}>
                        <VectorGraphic name="image-logo" className={$.logo} />
                      </div>
                      <Button
                        onClick={handleClose}
                        icon="icon-close"
                        variant="tertiary"
                        iconClassName={$.closeButtonModalIcon}
                        className={cn($.closeButtonModal, closeButtonClassName)}
                        ariaLabel={t('web_dialog_button_close')}
                      />
                    </div>
                  )}
                  <div className={cn($.content, contentClassName)}>
                    {children}

                    {withCancelButton && (
                      <Button variant="tertiary" onClick={handleClose}>
                        {t('web_general_button_cancel')}
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <motion.div
                  key="modal-content"
                  className={cn($.inner, {
                    [$.innerFullScreen]: isFullScreen,
                  })}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration,
                    delay: TRANSITION_DELAY_BASE / 1000,
                  }}
                >
                  <Button
                    className={cn($.closeButton, {
                      [$.closeButtonFullScreen]: isFullScreen,
                    })}
                    onClick={handleClose}
                    ariaLabel={t('web_dialog_button_close')}
                  >
                    <VectorGraphic name="icon-arrow-left" />
                  </Button>

                  {children}
                </motion.div>
              )}
            </motion.dialog>
          </RemoveScroll>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Dialog;
